import { LeftOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button, Card, Col, Divider, Flex, Row, Spin } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useClassifierServiceGetCommonClassifierElementListEndpointKey,
  useTreatmentServiceGetSidaCaseEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  GetCommonClassifierElementResponse
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Text, PageBreadcrumbs } from 'src/components'
import {
  EpidemiologyFinalDiagnosticDetailsCard,
  EpidemiologyNotificationDetailsCard,
  EpidemiologyPatientInformationDetailsCard,
  EpidemiologyPrimaryDiagnosticDetailsCard,
  EpidemiologyRegistrationDetailsCard,
  EpidemiologySymptomsDetailsCard
} from 'src/features/persons/pages/epidemiology-cases/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import {
  SidaMedicalPersonnelCard,
  SidaPatientIdentificationCard
} from '../components'
import { usePatientBreadcrumb } from '../hooks'

export const SidaNotificationDetailsPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()
  const { personId } = useParams()

  const {
    breadcrumbItems: items,
    isLoadingPerson,
    person
  } = usePatientBreadcrumb(personId)

  const { data: sidaCase, isLoading: isLoadingCase } =
    useTreatmentServiceGetSidaCaseEndpoint(
      { personId: String(personId) },
      [personId],
      { enabled: !!personId, retry: false }
    )

  const data = sidaCase?.form
  const epidemiologyForm = sidaCase?.epidemiologyForm

  const breadcrumbItems = [...items, { title: t('sida_notification.view') }]

  const classifierId = useClassifierId(
    ClassifierType.SIDA_AIDS_ESTABLISHMENT_REASON,
    ClassifierCategory.TREATMENT
  )

  const { data: reasonsList } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(classifierId)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getCommonClassifierElementListEndpoint(
          String(classifierId),
          pageParam,
          DEFAULT_PER_PAGE
        )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetCommonClassifierElementResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!classifierId
  })

  const reasonLabelById = useMemo(() => {
    const map = new Map<string, string>()
    reasonsList?.pages.forEach((page) =>
      page.data?.forEach((item: GetCommonClassifierElementResponse) => {
        if (item?.id)
          map.set(item.id, item?.translation?.[currentLang?.value] ?? item.id)
      })
    )
    return map
  }, [reasonsList, currentLang])

  const reasonsLabel = data?.aidsEstablishmentReasonIds?.length
    ? data.aidsEstablishmentReasonIds
        .map((id) => reasonLabelById.get(id) ?? id)
        .join(', ')
    : '-'

  if (isLoadingPerson || isLoadingCase) return <Spin spinning fullscreen />

  return (
    <PageBreadcrumbs
      title={t('sida_notification.view')}
      items={breadcrumbItems}
      extra={
        <Button
          type="primary"
          onClick={() =>
            navigate(paths.patients.sidaNotification.add(String(personId)))
          }
        >
          {t('common.edit')}
        </Button>
      }
      extraProps={{ align: 'flex-end' }}
    >
      <Button
        icon={<LeftOutlined />}
        className="fit-content"
        onClick={() => navigate(-1)}
      >
        {t('common.back')}
      </Button>

      <Divider />

      <Card>
        <Flex vertical gap={24}>
          {epidemiologyForm?.notification && (
            <EpidemiologyNotificationDetailsCard
              notification={epidemiologyForm.notification}
            />
          )}

          {epidemiologyForm?.primaryDiagnostic && (
            <EpidemiologyPrimaryDiagnosticDetailsCard
              primaryDiagnostic={epidemiologyForm.primaryDiagnostic}
            />
          )}

          {epidemiologyForm?.patient ? (
            <EpidemiologyPatientInformationDetailsCard
              patient={epidemiologyForm.patient}
            />
          ) : (
            <SidaPatientIdentificationCard person={person} />
          )}

          {epidemiologyForm?.registration && (
            <EpidemiologyRegistrationDetailsCard
              registration={epidemiologyForm.registration}
            />
          )}

          {epidemiologyForm?.symptoms && (
            <EpidemiologySymptomsDetailsCard
              symptoms={epidemiologyForm.symptoms}
            />
          )}

          {epidemiologyForm?.finalDiagnostic && (
            <EpidemiologyFinalDiagnosticDetailsCard
              finalDiagnostic={epidemiologyForm.finalDiagnostic}
            />
          )}

          <Card title={t('sida_notification.sida_diagnosis')}>
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <Flex vertical gap={4}>
                  <Text>{t('sida_notification.sida_stage_date')}</Text>
                  <Text weight={400}>
                    {data?.aidsStageEstablishedDate
                      ? dayjs(data.aidsStageEstablishedDate).format(
                          dayMonthYearFormatWithDot
                        )
                      : '-'}
                  </Text>
                </Flex>
              </Col>

              <Col span={8}>
                <Flex vertical gap={4}>
                  <Text>{t('sida_notification.sida_stage_reason')}</Text>
                  <Text weight={400}>{reasonsLabel}</Text>
                </Flex>
              </Col>

              <Col span={8}>
                <Flex vertical gap={4}>
                  <Text>
                    {t('sida_notification.arv_treatment_at_diagnosis')}
                  </Text>
                  <Text weight={400}>
                    {data?.hasArvTreatmentAtDiagnosis === true
                      ? t('sida_notification.yes')
                      : data?.hasArvTreatmentAtDiagnosis === false
                        ? t('sida_notification.no')
                        : '-'}
                  </Text>
                </Flex>
              </Col>

              <Col span={8}>
                <Flex vertical gap={4}>
                  <Text>{t('sida_notification.statistics_report_date')}</Text>
                  <Text weight={400}>
                    {data?.cspReportingDate
                      ? dayjs(data.cspReportingDate).format(
                          dayMonthYearFormatWithDot
                        )
                      : '-'}
                  </Text>
                </Flex>
              </Col>
            </Row>
          </Card>

          <SidaMedicalPersonnelCard savedDoctorName={data?.doctorFullName} />

          <Card title={t('form.notes')}>
            <Text weight={400}>{sidaCase?.notes || '-'}</Text>
          </Card>
        </Flex>
      </Card>
    </PageBreadcrumbs>
  )
}
